import { Paper, Table, TableBody, TableContainer } from "@mui/material";
import { useAtomValue } from "jotai";
import { noop } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { baseAtom } from "../../stores/jotai/base";
import CohortStatsTableHead from "./CohortStatsTableHead";
import CohortStatsTableRow from "./CohortStatsTableRow";
import { COHORT_SORT_PARAMS } from "./constants";
import { STOCK_THEMES } from "../../util/stockThemes";

// TODO: once the backend removes the offset from the data
// we can remove it from the frontend too
const OFFSET_USED_TO_PREVENT_RENDERING_OLD_CHART = 0.1;

const CohortStatsTable = (props) => {
    const { columns, cohortStats } = props;
    const { cohort } = useAtomValue(baseAtom)
    const [sortParams, setSortParams] = useState({
        sortField: COHORT_SORT_PARAMS.FIELD_IDS.TEAM_NAME,
        direction: COHORT_SORT_PARAMS.DIRECTIONS.ASC,
    });
    const [columnsWithSortInfo, setColumnsWithSortInfo] = useState([]);
    const [modifiedCohortStats, setModifiedCohortStats] = useState([]);

    const handleSort = () => {
        const { sortField, direction } = sortParams;

        setModifiedCohortStats((prevStats) => {
            const newStats = [...prevStats];

            return newStats.sort((stat1, stat2) => {
                if (direction === COHORT_SORT_PARAMS.DIRECTIONS.ASC) {
                    return stat1?.[sortField] > stat2?.[sortField] ? 1 : -1;
                } else {
                    return stat1?.[sortField] < stat2?.[sortField] ? 1 : -1;
                }
            });
        });
    };

    useEffect(() => {
        handleSort();
    }, [sortParams]);

    useEffect(() => {
        setColumnsWithSortInfo(
            columns?.map((column) => {
                const isActive = sortParams?.sortField === column?.fieldKey;
                const sortMethod = (fieldKey) => {
                    let direction = COHORT_SORT_PARAMS.DIRECTIONS.ASC;

                    if (sortParams.sortField === fieldKey) {
                        direction =
                            sortParams.direction === COHORT_SORT_PARAMS.DIRECTIONS.ASC
                                ? COHORT_SORT_PARAMS.DIRECTIONS.DESC
                                : COHORT_SORT_PARAMS.DIRECTIONS.ASC;
                    }

                    setSortParams({
                        sortField: fieldKey,
                        direction,
                    });
                };

                return {
                    ...column,
                    sort: {
                        active: isActive,
                        direction: isActive
                            ? sortParams.direction
                            : COHORT_SORT_PARAMS.DIRECTIONS.ASC,
                        method: column?.sortable ? sortMethod : noop,
                    },
                };
            })
        );
    }, [columns, sortParams]);

    const sortedCohortPeriodGoals = useMemo(() => {
        return cohort?.periods
            .sort((period1, period2) => {
                //@ts-ignore
                const period1EndDate = moment(period1?.endDate);
                //@ts-ignore
                const period2EndDate = moment(period2?.endDate);

                if (period1EndDate.isAfter(period2EndDate)) {
                    return 1;
                } else {
                    return -1;
                }
            })
            .map((period) => period?.goal);
    }, [cohort?.periods]);

    useEffect(() => {
        setModifiedCohortStats(cohortStats);
    }, [cohortStats]);

    return (
        <TableContainer
            component={Paper}
            sx={{
                border: "1px solid #c2c2c2",
                flexGrow: 1,
            }}
        >
            <Table stickyHeader={true}>
                <CohortStatsTableHead
                    columns={columnsWithSortInfo}
                    setSortParams={setSortParams}
                />
                <TableBody>
                    {modifiedCohortStats?.map((stat, idx) => {
                        const id = `${stat?.teamName}-intvw_${stat?.interviews}-insght_${stat?.insights}-hyp_${stat?.hypotheses}-invld_hyp_${stat?.invalidatedHypotheses}-vld_hyp_${stat?.validatedHypotheses}`;
                        const finalWeekChart = {
                            ...stat?.week_chart,
                            series: [
                                {
                                    ...stat?.week_chart?.series?.[0],
                                    data: stat?.week_chart?.series?.[0]?.data?.map(
                                        (interviewCount, index) => ({
                                            // reason behind this check: https://app.asana.com/0/0/1205725658017442/1205908418988855/f
                                            y: interviewCount === 0.1 ? 0 : interviewCount,
                                            color:
                                                interviewCount >=
                                                sortedCohortPeriodGoals[index]
                                                    ? STOCK_THEMES.v3?.palette?.success?.main
                                                    : STOCK_THEMES.v3?.palette?.error?.main,
                                        })
                                    ),
                                },
                            ],
                            yAxis: {
                                ...stat?.week_chart.yAxis,
                                min: 0,
                                max: Math.max(...(stat?.week_chart?.series?.[0]?.data ?? [0])),
                            },
                        };

                        return (
                            <CohortStatsTableRow
                                key={id}
                                index={idx}
                                hypotheses={stat?.hypotheses}
                                insights={stat?.insights}
                                interviews={stat?.interviews}
                                invalidatedHypotheses={stat?.invalidatedHypotheses}
                                teamName={stat?.teamName}
                                validatedHypotheses={stat?.validatedHypotheses}
                                week_chart={finalWeekChart}
                                columns={columns}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CohortStatsTable;
